import React, { useState, useEffect } from 'react';
import TickerLink from './TickerLink.jsx';
import { formatMoney } from './rangeBar.js';
import './terminal.css';
import './AiCuratedPicksCards.css';

// `url` (picks JSON) and `analysisUrl` (top-15 markdown) are signed links
// from the members API manifest for the active market: undefined while the
// manifest loads, null when the file is missing. Nothing here names a bucket.
const AICuratedPicksCards = ({ market = 'sp500', url, analysisUrl }) => {
  const [picks, setPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState('');

  // Refetch whenever the manifest hands over a new link (market switch)
  useEffect(() => {
    if (url === undefined) {
      setLoading(true);
      return;
    }
    if (url === null) {
      setError('AI picks are not available');
      setLoading(false);
      return;
    }
    fetchAIPicks(url);
  }, [url]);

  const fetchAIPicks = async (signedUrl) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(signedUrl);

      if (!response.ok) {
        throw new Error('Failed to fetch AI picks');
      }

      const data = await response.json();

      setPicks(data.top_picks || []);
      setLastUpdated(data.generated_display || 'Recently');
      setLoading(false);
    } catch (error) {
      console.error('Error fetching AI picks:', error.message);
      setError(error.message);
      setLoading(false);
    }
  };

  // Risk is a signal: low = green, medium = amber, high = red; unknown = muted.
  const riskClass = (risk) => {
    if (!risk) return 'is-unknown';
    const r = risk.toLowerCase();
    if (r.includes('low')) return 'is-low';
    if (r.includes('medium')) return 'is-medium';
    return 'is-high';
  };

  const discountValue = (d) => {
    const n = parseFloat(d);
    return Number.isFinite(n) ? n : null;
  };

  if (loading) {
    return (
      <div className="ai-picks-cards">
        <div className="ai-picks-loading">
          <div className="spinner"></div>
          <p>Loading AI-curated picks...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ai-picks-cards">
        <div className="ai-picks-error">
          <p>Unable to load AI picks. Please try again later.</p>
        </div>
      </div>
    );
  }

  if (!picks || picks.length === 0) {
    return null;
  }

  return (
    <div className="ai-picks-cards">
      <div className="ai-picks-header">
        <div className="ai-picks-title-row">
          <div>
            <h2>Top 5 AI-Curated Picks This Week</h2>
            <p className="ai-picks-subtitle">
              Selected from the {market === 'nasdaq' ? 'NASDAQ' : 'S&P 500'} Quality + Value screen (A+ to B grades, 15%+ discount)
            </p>
          </div>
          <div className="ai-picks-meta">
            <span className="t-badge">Powered by Claude</span>
            <span className="t-secondary">Updated <span className="t-num">{lastUpdated}</span></span>
            {analysisUrl && (
              <a
                href={analysisUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="t-link"
              >
                Full top-15 analysis →
              </a>
            )}
          </div>
        </div>
      </div>

      <div className="picks-cards-grid">
        {picks.map((pick, index) => {
          const discount = discountValue(pick.discount);
          return (
            <div key={index} className={`pick-card ${index === 0 ? 'featured' : ''}`}>
              <div className="pick-head">
                <div className="pick-ticker"><TickerLink ticker={pick.ticker} /></div>
                <div className="pick-rank t-mono">#{index + 1}</div>
              </div>
              <div className="pick-company">{pick.company}</div>

              <div className="pick-metrics">
                <div className="pick-metric">
                  <span className="t-label">Price</span>
                  <span className="t-value">{formatMoney(pick.price)}</span>
                </div>
                <div className="pick-metric">
                  <span className="t-label">FFV</span>
                  <span className="t-value">{formatMoney(pick.ffv)}</span>
                </div>
                <div className="pick-metric">
                  <span className="t-label">Discount</span>
                  <span className={`t-value t-value--lg ${discount === null ? '' : discount >= 0 ? 't-up' : 't-down'}`}>
                    {discount === null ? pick.discount : `${discount.toFixed(1)}%`}
                  </span>
                </div>
                <div className="pick-metric">
                  <span className="t-label">Risk</span>
                  <span className={`t-value pick-risk ${riskClass(pick.risk)}`}>
                    <span className="pick-risk__dot" aria-hidden="true" />
                    {pick.risk || '—'}
                  </span>
                </div>
              </div>

              {pick.thesis && (
                <blockquote className="t-quote t-quote--clamp pick-thesis" title={pick.thesis}>
                  {pick.thesis}
                </blockquote>
              )}
            </div>
          );
        })}
      </div>

      <div className="ai-picks-footer">
        <p className="disclaimer">
          AI-generated analysis for research purposes only. Not investment advice.
        </p>
      </div>
    </div>
  );
};

export default AICuratedPicksCards;
